/* AIROTECH Insights — topical authority content.
   Source of truth for the article pages, prerender and sitemap.
   Content is original and authored by the AIROTECH team. No fabricated
   clients, statistics, reviews, awards, partnerships or case studies. */

export const INSIGHT_CATEGORIES = ["AI", "Software Engineering", "Product Strategy"];

export const INSIGHTS = [
  {
    slug: "ai-transformation-guide",
    title: "AI Transformation: A Practical Guide for Businesses",
    description:
      "A practical, hype-free guide to AI transformation — what it means, where it creates leverage, how to sequence it, and how to ship AI that survives real users and real data.",
    category: "AI",
    topics: ["AI transformation", "AI strategy", "operations", "change management"],
    author: "The AIROTECH Team",
    publishDate: "2026-04-14",
    updatedDate: "2026-06-03",
    readMins: 9,
    kind: "pillar",
    clusterSlugs: ["ai-transformation", "ai-consulting", "ai-automation", "ai-agents"],
    relatedServices: ["ai-transformation", "ai-consulting", "ai-automation"],
    relatedArticles: ["ai-agents-vs-ai-automation"],
    excerpt:
      "AI transformation is not a chatbot bolted onto an old workflow. It's redesigning how work happens around AI where it genuinely creates leverage — and shipping it into production.",
    answer:
      "AI transformation is the process of redesigning core business processes around artificial intelligence to reduce cost, speed up work, and unlock new capabilities. Done well, it ends in shipped, monitored software — not experiments — and is sequenced around the few workflows where AI moves real numbers.",
    sections: [
      {
        h: "What is AI transformation?",
        p: [
          "AI transformation is the deliberate redesign of how your business operates so that artificial intelligence does the work it is genuinely good at — classification, extraction, drafting, summarization, prediction and multi-step task execution — inside your real workflows.",
          "The distinction that matters: a pilot proves something is possible; a transformation changes how work actually happens day to day. Most organizations get stuck in permanent pilot mode because they treat AI as a demo instead of a system with guardrails, evaluation and human oversight.",
        ],
      },
      {
        h: "Where does AI actually create leverage?",
        p: [
          "AI pays back fastest in high-volume, judgment-light tasks and in places where humans are the bottleneck rather than the value. Before writing any code, map your workflows, data and cost centers and look for the two or three places where AI meaningfully moves a number you care about.",
        ],
        list: [
          "High-volume document and data processing (intake, extraction, routing).",
          "Drafting and summarization where a human still reviews the output.",
          "Support and internal knowledge retrieval over your own content.",
          "Decision support where speed matters more than a perfect answer.",
        ],
      },
      {
        h: "How do you sequence an AI transformation?",
        p: [
          "Sequence by impact and risk, not by novelty. Ship the single highest-leverage system first, instrument it, prove the return, and use that momentum — and the lessons — to fund the next phase. A big-bang rewrite is the most common way transformations fail.",
        ],
        sub: [
          { h: "1. Assess", p: ["A focused audit of workflows, data and cost centers to find where AI creates measurable leverage."] },
          { h: "2. Build one thing well", p: ["Ship a single production system with evaluation and human-in-the-loop review, not a portfolio of demos."] },
          { h: "3. Measure and expand", p: ["Define the metric before you build — time saved, cost reduced, conversion lifted — then instrument it and expand from evidence."] },
        ],
      },
      {
        h: "Do you need clean data first?",
        p: [
          "No. Waiting for a perfect data foundation is a common way to never start. Work with the data you have, identify the gaps that actually block the use case, and build data hygiene into the roadmap rather than blocking on it.",
        ],
      },
      {
        h: "Why most AI projects stall before production",
        p: [
          "The gap between a working prompt and a trustworthy production system is where most projects die. Production AI needs evaluation sets, quality gates, cost controls, observability and a clear human-oversight model. If those aren't part of the plan from day one, results rarely survive contact with real users.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long does an AI transformation take?",
        a: "Start with a focused assessment (typically one to two weeks), then ship the first high-leverage system in weeks rather than quarters, and expand from there. The full journey depends on scope, but value should arrive early.",
      },
      {
        q: "Is AI transformation only for large enterprises?",
        a: "No. Smaller businesses often see faster returns because they have fewer approval layers and can redesign a workflow end to end. The principle is the same at any size: find the leverage, ship one thing well, measure it.",
      },
      {
        q: "What's the difference between AI transformation and digital transformation?",
        a: "Digital transformation modernizes systems and processes with software generally; AI transformation specifically redesigns work around AI capabilities. They overlap, and the best programs treat AI as one powerful tool inside a broader modernization effort.",
      },
    ],
  },

  {
    slug: "how-to-build-an-app",
    title: "How to Build an App: The Complete Idea-to-Launch Guide",
    description:
      "A clear, end-to-end guide to building an app — choosing the right platform (native, web or PWA), scoping the first version, designing, engineering, launching and iterating.",
    category: "Product Strategy",
    topics: ["app development", "mobile apps", "product design", "launch"],
    author: "The AIROTECH Team",
    publishDate: "2026-04-28",
    updatedDate: "2026-06-05",
    readMins: 11,
    kind: "pillar",
    clusterSlugs: ["app-development", "mobile-app-development", "pwa-development", "mvp-development"],
    relatedServices: ["app-development", "mobile-app-development", "mvp-development"],
    relatedArticles: ["how-to-scope-an-mvp", "software-development-cost"],
    excerpt:
      "Building an app is less about code and more about decisions: what to build first, which platform, and how to launch something real without cutting the wrong corners.",
    answer:
      "To build an app, work through six stages: define the core problem and audience, choose the right platform (native, web or PWA), scope a focused first version, design the experience before engineering it, build with testing and analytics in place, then launch and iterate on real usage. The biggest risk is not technical — it's building too much before validating that people want it.",
    sections: [
      {
        h: "Start with the problem, not the features",
        p: [
          "Every app that struggles has the same root cause: it was defined as a list of features instead of a solution to a specific problem for a specific person. Before anything else, write one sentence: who is this for, and what can they do with it that they couldn't before.",
          "That sentence becomes the filter for every later decision. If a feature doesn't serve it, it waits.",
        ],
      },
      {
        h: "Native, web, or PWA — how to choose",
        p: [
          "The platform decision should follow your users and goals, not what's easiest to build. Each path has a clear sweet spot.",
        ],
        sub: [
          { h: "Native (iOS / Android)", p: ["Best when you need peak performance, deep device features (camera, sensors, offline-first), and app store presence. Highest cost, highest polish."] },
          { h: "Web application", p: ["Best for tools, dashboards and platforms people use at a desk. Instant updates, no install, reachable by a link."] },
          { h: "Progressive web app (PWA)", p: ["Best when you want app-like, installable, offline-capable experience across devices without app store friction — often the fastest, most cost-effective cross-platform path."] },
        ],
      },
      {
        h: "Scope the first version ruthlessly",
        p: [
          "The first version should prove one loop of value, not everything you imagine. Ruthless scope isn't about building a worse product — it's about learning the truth faster and building the winning version on solid ground. See our guide on scoping an MVP for the full method.",
        ],
      },
      {
        h: "Design before you engineer",
        p: [
          "High-fidelity design first means you see and feel the product before it's expensive to change. It surfaces the hard UX questions early, aligns everyone on the same vision, and makes engineering faster because the target is clear.",
        ],
      },
      {
        h: "Build with quality and measurement built in",
        p: [
          "Testing, review, observability and analytics are part of the build, not a scramble before launch. Instrument the core loop from day one so your first release teaches you where real users get stuck.",
        ],
      },
      {
        h: "Launch, then earn the roadmap",
        p: [
          "Launch is the start of learning, not the finish line. Ship, watch how people actually behave, and let evidence — not opinion — drive what you build next. The apps that win are the ones that iterate on reality.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much does it cost to build an app?",
        a: "It depends almost entirely on scope and complexity. A focused first version costs far less than a full multi-user platform. The honest answer comes from scoping the idea first — our cost framework article explains how to estimate responsibly.",
      },
      {
        q: "Should I build for iOS and Android at the same time?",
        a: "Often, but not always. If your audience is split across both and you need native performance, cross-platform or dual native makes sense. If reach and speed matter more, a PWA can serve both from one codebase.",
      },
      {
        q: "How long does it take to build an app?",
        a: "A focused first version can ship in weeks; a full platform takes longer. Timeline is a function of scope, which is exactly why scoping well is the highest-leverage decision you make.",
      },
    ],
  },

  {
    slug: "custom-software-development-guide",
    title: "Custom Software Development: A Complete Guide",
    description:
      "When custom software is worth it, how it differs from off-the-shelf tools, what a good build process looks like, and how to protect your investment with clean architecture and ownership.",
    category: "Software Engineering",
    topics: ["custom software", "architecture", "integrations", "ownership"],
    author: "The AIROTECH Team",
    publishDate: "2026-05-06",
    updatedDate: "2026-06-06",
    readMins: 10,
    kind: "pillar",
    clusterSlugs: ["custom-software-development", "software-development", "web-application-development", "saas-development"],
    relatedServices: ["custom-software-development", "software-development", "web-application-development"],
    relatedArticles: ["software-development-cost"],
    excerpt:
      "Off-the-shelf tools force your business to bend around their limits. Custom software does the opposite — but only when the fit is worth the investment.",
    answer:
      "Custom software is software built specifically for how your business operates, rather than a generic product you adapt to. It's worth building when your workflow is a competitive advantage, when off-the-shelf tools create manual work, or when rising per-seat fees buy a poor fit. A good build gives you clean architecture, real integrations, and ownership of the code and roadmap.",
    sections: [
      {
        h: "What is custom software development?",
        p: [
          "Custom software development is the design and engineering of applications built around your specific processes, data and users — internal tools, platforms, portals and systems that fit exactly how you work.",
          "The opposite is off-the-shelf software: a generic product you configure and adapt to. Both are valid; the question is where the fit matters enough to justify building.",
        ],
      },
      {
        h: "When is custom software worth it?",
        p: ["Custom software earns its cost in a few clear situations."],
        list: [
          "Your workflow is a competitive advantage and no generic tool captures it.",
          "Off-the-shelf tools force manual work, double entry or awkward workarounds.",
          "You're paying rising per-seat fees for software that fits poorly.",
          "You need integrations or data ownership that SaaS products won't give you.",
        ],
      },
      {
        h: "What does a good build process look like?",
        p: [
          "Reliable software isn't about heroics; it's about a disciplined process where the people who scope the work are the people who build it, so context never gets lost.",
        ],
        sub: [
          { h: "Discovery and scope", p: ["Map the real process — including the exceptions and edge cases — before designing the system."] },
          { h: "Architecture and data design", p: ["Design for where you're going: clean data models, sensible services and room to grow features and users."] },
          { h: "Build, test, and observe", p: ["Testing, code review and observability are part of the process, not an afterthought bolted on before launch."] },
          { h: "Deploy and support", p: ["Ship with CI/CD and monitoring, then support and evolve the system after launch."] },
        ],
      },
      {
        h: "Do you own the code?",
        p: [
          "You should. A key advantage of custom software is that you own the intellectual property and the roadmap — no per-seat lock-in, no waiting on someone else's feature backlog. Confirm ownership terms before you start; at AIROTECH, you own your code.",
        ],
      },
      {
        h: "How to protect the investment",
        p: [
          "The difference between software that lasts and software that gets rewritten is architecture and quality discipline. Build on clean data models, integrate with the tools you already use, and keep tests and documentation current so the system stays maintainable as it grows.",
        ],
      },
    ],
    faqs: [
      {
        q: "Custom software vs. off-the-shelf — which is cheaper?",
        a: "Off-the-shelf is usually cheaper up front; custom is often cheaper over time when the alternative is manual work, poor fit, or escalating per-seat fees. Compare total cost of ownership, not just the initial price.",
      },
      {
        q: "How long does custom software take to build?",
        a: "It depends on scope. A focused internal tool ships quickly; a full platform takes longer. A good partner scopes the work with you before committing to a timeline.",
      },
      {
        q: "Can custom software integrate with our existing tools?",
        a: "Yes. Well-built custom software connects to your existing CRM, payments, cloud and databases so everything works as one system rather than a set of disconnected apps.",
      },
    ],
  },

  {
    slug: "how-to-scope-an-mvp",
    title: "How to Scope an MVP Without Building the Wrong Thing",
    description:
      "A practical method for scoping a minimum viable product: find the one loop of value, cut everything else, and build something real you won't have to throw away.",
    category: "Product Strategy",
    topics: ["MVP", "scope", "validation", "startups"],
    author: "The AIROTECH Team",
    publishDate: "2026-05-18",
    updatedDate: "2026-06-04",
    readMins: 7,
    kind: "supporting",
    pillarSlug: "how-to-build-an-app",
    relatedServices: ["mvp-development", "startup-software-development"],
    relatedArticles: ["how-to-build-an-app", "software-development-cost"],
    excerpt:
      "An MVP should answer one question: is this worth building more of? Scoping is how you get that answer fast without wasting runway.",
    answer:
      "To scope an MVP, identify the single loop of value your product must prove, cut every feature that doesn't serve it, and build that loop well enough for real users. A good MVP is focused, not flimsy — it's built on a foundation the winning version can grow from, not a throwaway prototype.",
    sections: [
      {
        h: "What is an MVP, really?",
        p: [
          "A minimum viable product is the smallest version of your idea that lets real users get real value — and lets you learn whether the idea is worth pursuing further. It is minimum in scope and viable in quality; the mistake is treating it as minimum in quality.",
        ],
      },
      {
        h: "Find the one loop of value",
        p: [
          "Every product has a core loop: the sequence a user repeats to get value. For a marketplace it might be list, discover, transact. For a tool it might be import, act, get result. Your MVP is that loop and almost nothing else.",
          "Write the loop down. Everything outside it — settings, edge cases, nice-to-haves — waits until the loop is proven.",
        ],
      },
      {
        h: "Cut with a single question",
        p: [
          "For every proposed feature, ask: does the core loop work without it for a first real user? If yes, it's out of the MVP. This one question resolves most scope arguments faster than any prioritization framework.",
        ],
      },
      {
        h: "Build real, not throwaway",
        p: [
          "Fast doesn't mean disposable. Architect the MVP so the successful version scales from the same foundation. Cutting scope is smart; cutting engineering quality just means you rebuild the moment it works.",
        ],
      },
      {
        h: "Instrument it to learn",
        p: [
          "Bake in analytics and a feedback path from day one. The point of an MVP is evidence — where people succeed, where they drop off, and whether they come back. Ship it, watch it, and let the data set your next scope.",
        ],
      },
    ],
    faqs: [
      {
        q: "How fast can you build an MVP?",
        a: "Focused MVPs ship in weeks. The exact timeline depends on how tightly the scope is drawn — which is the whole point of scoping well first.",
      },
      {
        q: "Will we have to rebuild after the MVP?",
        a: "Not if it's built right. When the MVP is architected for growth, the successful version scales from the same foundation instead of starting over.",
      },
      {
        q: "What's the difference between an MVP and a prototype?",
        a: "A prototype demonstrates an idea, often disposable; an MVP is a real, usable product for real users. Prototypes test feasibility or design; MVPs test whether the product is worth building more of.",
      },
    ],
  },

  {
    slug: "ai-agents-vs-ai-automation",
    title: "AI Agents vs. AI Automation: What's the Difference?",
    description:
      "AI agents and AI automation solve different problems. This guide explains the difference, when to use each, and how to deploy both safely with guardrails and evaluation.",
    category: "AI",
    topics: ["AI agents", "AI automation", "workflows", "guardrails"],
    author: "The AIROTECH Team",
    publishDate: "2026-05-22",
    updatedDate: "2026-06-05",
    readMins: 7,
    kind: "supporting",
    pillarSlug: "ai-transformation-guide",
    relatedServices: ["ai-agents", "ai-automation"],
    relatedArticles: ["ai-transformation-guide"],
    excerpt:
      "The terms get used interchangeably, but agents and automation are different tools for different jobs. Knowing which you need saves time and money.",
    answer:
      "AI automation uses AI to handle well-defined, repetitive tasks within a fixed workflow — classify, extract, route, draft. AI agents plan and take multi-step actions using tools and data to complete a goal that isn't fully scripted. Automation is best when the steps are known; agents are best when the path varies. Both need clear boundaries, guardrails and evaluation in production.",
    sections: [
      {
        h: "What is AI automation?",
        p: [
          "AI automation applies AI to specific, high-volume tasks inside a defined workflow — classifying a document, extracting fields, routing a request, drafting a reply. The workflow is fixed; AI handles the judgment-light steps within it, usually with human review where accuracy matters.",
        ],
      },
      {
        h: "What is an AI agent?",
        p: [
          "An AI agent can plan and take multiple steps to accomplish a goal, deciding which tools to call and in what order rather than following a fixed script. Agents are useful when the path to the outcome varies — for example, researching across sources, then acting on the result.",
        ],
      },
      {
        h: "When to use which",
        p: ["Choose based on how predictable the work is."],
        list: [
          "Known, repeatable steps and high volume → AI automation.",
          "Variable path, multiple tools, a goal rather than a script → AI agent.",
          "A mix → an automated workflow that calls an agent for the open-ended step.",
        ],
      },
      {
        h: "Deploying either one safely",
        p: [
          "Both need the same discipline in production: clear boundaries on what the system can and can't do, controlled access to tools and data, guardrails, and an evaluation harness so behavior is measured and improved rather than hoped for. Agents especially need tight scoping because they take actions.",
        ],
      },
    ],
    faqs: [
      {
        q: "Are AI agents safe to deploy?",
        a: "They can be, with proper scoping, permissions, guardrails and evaluation. The risk comes from giving an agent broad, unmonitored access; the solution is bounded tools and measured behavior.",
      },
      {
        q: "Will AI automation replace our team?",
        a: "It removes tedious, repetitive work so your team focuses on judgment and higher-value tasks. Well-designed automation augments people with human review where it matters, rather than replacing them blindly.",
      },
    ],
  },

  {
    slug: "software-development-cost",
    title: "How Much Does It Cost to Build Software? A Practical Framework",
    description:
      "A responsible framework for estimating software cost — the drivers that actually move the number, why ranges beat false precision, and how scoping controls the budget.",
    category: "Software Engineering",
    topics: ["cost", "estimation", "budgeting", "scope"],
    author: "The AIROTECH Team",
    publishDate: "2026-05-30",
    updatedDate: "2026-06-06",
    readMins: 8,
    kind: "supporting",
    pillarSlug: "custom-software-development-guide",
    relatedServices: ["custom-software-development", "mvp-development"],
    relatedArticles: ["custom-software-development-guide", "how-to-build-an-app"],
    excerpt:
      "Anyone who quotes a price before understanding your scope is guessing. Here's the honest framework for what actually drives software cost.",
    answer:
      "Software cost is driven by scope and complexity, not by a fixed price list. The main drivers are the number and depth of features, integrations, data and security requirements, design fidelity, and how much is genuinely new versus well-trodden. The responsible way to estimate is to scope the idea first, then give a range that narrows as decisions are made.",
    sections: [
      {
        h: "Why there's no single price",
        p: [
          "Two apps that sound similar in a sentence can differ by an order of magnitude in cost, because the sentence hides the complexity. A responsible estimate starts by understanding what you're actually building, not by pattern-matching to a headline number.",
        ],
      },
      {
        h: "The drivers that actually move the number",
        p: ["Focus your thinking on the factors that dominate a software budget."],
        list: [
          "Scope: how many features, and how deep each one goes.",
          "Integrations: every external system you connect adds surface area.",
          "Data and security: sensitive data, roles and compliance raise the bar.",
          "Design fidelity: polished, custom experiences cost more than standard patterns.",
          "Novelty: genuinely new problems carry more uncertainty than well-solved ones.",
        ],
      },
      {
        h: "Ranges beat false precision",
        p: [
          "Early in a project, a range is more honest than a single number. As scope decisions are made — what's in the first version, which integrations, how much custom design — the range narrows. Beware anyone who commits to an exact price before those decisions exist.",
        ],
      },
      {
        h: "How scoping controls the budget",
        p: [
          "The most powerful cost lever is scope. Deciding what to build first — and what can wait — is how you fit a real product into a real budget. This is why we scope every idea through a guided assessment before talking price: it turns a vague question into an honest answer.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much does it cost to build an MVP?",
        a: "Far less than a full platform, because an MVP is scoped to one loop of value. The exact figure depends on that scope, which is why scoping first is the key to a realistic budget.",
      },
      {
        q: "Why won't you give me a price immediately?",
        a: "Because an honest price requires understanding your scope. Quoting before that is guessing, and guesses lead to the overruns and rebuilds that give software projects a bad name. We scope first, then price with confidence.",
      },
      {
        q: "What makes software more expensive than expected?",
        a: "Usually scope creep and hidden complexity — integrations, edge cases, security and design detail that weren't visible in the original sentence. Disciplined scoping and clear phases keep the budget under control.",
      },
    ],
  },
];

export const INSIGHT_BY_SLUG = Object.fromEntries(INSIGHTS.map((a) => [a.slug, a]));

export function relatedInsights(slug, limit = 3) {
  const a = INSIGHT_BY_SLUG[slug];
  if (!a) return [];
  const explicit = (a.relatedArticles || []).map((s) => INSIGHT_BY_SLUG[s]).filter(Boolean);
  if (explicit.length >= limit) return explicit.slice(0, limit);
  const byTopic = INSIGHTS.filter(
    (x) => x.slug !== slug && !explicit.includes(x) && x.topics.some((t) => a.topics.includes(t))
  );
  return [...explicit, ...byTopic].slice(0, limit);
}
